// SchlossHinweis.tsx — Hinweis an der Klickstelle, wenn jemand in der
// gesperrten Akte etwas bearbeiten will (User 30.09.2026, Plan M2 in
// docs/superpowers/plans/2026-09-30-themen-uebersicht.md).
//
// Die Akte sperrt ihren Reiter „Allgemein" mit `<fieldset disabled>`.
// Deaktivierte Felder schlucken den Klick — der Nutzer klickt, und nichts
// passiert. Deshalb:
//   - `SchlossRahmen` legt sich um das gesperrte fieldset. Per CSS
//     (contactRedesign.css, `.nk-schloss-rahmen`) sind die gesperrten Felder
//     `pointer-events: none`; der Klick landet so beim Rahmen.
//   - Der Rahmen zeigt genau dort einen kleinen Hinweis: „Zum Bearbeiten oben
//     auf das Schloss klicken" — und lässt das Schloss kurz pulsieren
//     (`onSchlossHinweis`).
//   - Ohne Schreibrecht gibt es kein Schloss: dann `onGesperrtOhneRecht`
//     (Dialog der Akte) oder, ohne ihn, der Hinweis „Keine Berechtigung zum
//     Bearbeiten".
// Links, Namen-Sprünge (`KontaktName`), `AnsichtsKnopf`-Aktionen (Anrufen,
// Mail, Karte) und das Auf-/Zuklappen der Karten bleiben klickbar und lösen
// keinen Hinweis aus. Ebenso die markierbaren Werte (`SperrWert`, T-62) und
// jeder Doppel-/Dreifachklick: Wer markiert, will kopieren, nicht bearbeiten.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { alleDokumente, aktiveAuswahl, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

export const SCHLOSS_HINWEIS_TEXT = "Zum Bearbeiten oben auf das Schloss klicken";
export const KEINE_RECHTE_TEXT = "Keine Berechtigung zum Bearbeiten";

/** Wie lange der Hinweis steht (ms). */
export const HINWEIS_DAUER = 2000;

/** Klickflächen, die auch gesperrt leben — dort kein Hinweis. */
const LEBENDIG = 'a[href], [role="button"], [role="link"], [role="menuitem"], [role="option"], [role="tab"]';

const STIL: React.CSSProperties = {
  position: "fixed",
  zIndex: 10000,
  transform: "translate(-50%, calc(-100% - 10px))",
  padding: "6px 10px",
  borderRadius: 8,
  background: "#2b1d28",
  color: "#fff",
  font: "600 12.5px/1.3 system-ui, -apple-system, 'Segoe UI', sans-serif",
  boxShadow: "0 6px 18px rgba(0, 0, 0, 0.22)",
  pointerEvents: "none",
  whiteSpace: "nowrap",
  maxWidth: "calc(100vw - 16px)",
};

/** Der Hinweis selbst — per Portal an `x`/`y` (Viewport-Koordinaten). */
export function SchlossHinweis({
  x,
  y,
  text,
  onClose,
  dauer = HINWEIS_DAUER,
}: {
  x: number;
  y: number;
  text: string;
  onClose: () => void;
  dauer?: number;
}) {
  const schliessenRef = React.useRef(onClose);
  schliessenRef.current = onClose;
  React.useEffect(() => {
    const t = window.setTimeout(() => schliessenRef.current(), dauer);
    // Klick irgendwo anders schließt ihn sofort. Erst ab dem nächsten Klick —
    // der auslösende läuft gerade noch durch.
    const weg = () => schliessenRef.current();
    const an = window.setTimeout(() => alleDokumente.addEventListener("pointerdown", weg, true), 0);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(an);
      alleDokumente.removeEventListener("pointerdown", weg, true);
    };
  }, [x, y, text, dauer]);

  // Nicht aus dem Fenster ragen lassen (grob — die Breite kennt nur das CSS).
  const breite = typeof window !== "undefined" ? aktivesFenster().innerWidth : 0;
  const left = breite ? Math.min(Math.max(x, 140), Math.max(breite - 140, 140)) : x;
  const top = Math.max(y, 44);

  return createPortal(
    <div role="status" aria-live="polite" className="nk-schloss-hinweis" style={{ ...STIL, left, top }}>
      {text}
    </div>,
    document.body,
  );
}

type Hinweis = { x: number; y: number; text: string; n: number };

/**
 * Rahmen um den gesperrten Reiter „Allgemein".
 *
 * `gesperrt`: Schloss zu (mit Schreibrecht). `ohneRecht`: kein Schreibrecht —
 * das Formular ist dann immer gesperrt, ein Schloss gibt es nicht.
 */
export function SchlossRahmen({
  gesperrt,
  ohneRecht = false,
  onSchlossHinweis,
  onGesperrtOhneRecht,
  children,
}: {
  gesperrt: boolean;
  ohneRecht?: boolean;
  /** Der Hinweis erscheint — die Akte lässt das Schloss pulsieren. */
  onSchlossHinweis?: () => void;
  /** Ohne Schreibrecht: statt des Hinweises (z. B. Dialog „Keine Rechte"). */
  onGesperrtOhneRecht?: () => void;
  children: React.ReactNode;
}) {
  const [hinweis, setHinweis] = React.useState<Hinweis | null>(null);
  const aktiv = gesperrt || ohneRecht;

  // Schloss auf → ein stehender Hinweis ist überholt.
  React.useEffect(() => {
    if (!aktiv) setHinweis(null);
  }, [aktiv]);

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!aktiv) return;
    const ziel = e.target as Element | null;
    if (!ziel?.closest) return;
    // Nur Klicks in die gesperrte Maske, nicht auf lebendige Klickflächen.
    if (!ziel.closest("fieldset[disabled]")) return;
    if (ziel.closest(LEBENDIG)) return;
    // Markierbarer Wert eines gesperrten Feldes (T-62) und Mehrfachklick
    // (Wort/Wert markieren): kopieren, nicht bearbeiten.
    if (ziel.closest(".nk-sperrwert")) return;
    if (e.detail > 1) return;
    // Wer Text markiert (zum Kopieren), will nichts bearbeiten.
    const auswahl = typeof window.getSelection === "function" ? aktiveAuswahl() : null;
    if (auswahl && !auswahl.isCollapsed && auswahl.toString().trim()) return;

    if (ohneRecht) {
      if (onGesperrtOhneRecht) {
        onGesperrtOhneRecht();
        return;
      }
      setHinweis((h) => ({ x: e.clientX, y: e.clientY, text: KEINE_RECHTE_TEXT, n: (h?.n ?? 0) + 1 }));
      return;
    }
    setHinweis((h) => ({ x: e.clientX, y: e.clientY, text: SCHLOSS_HINWEIS_TEXT, n: (h?.n ?? 0) + 1 }));
    onSchlossHinweis?.();
  };

  return (
    <div className="nk-schloss-rahmen" data-gesperrt={aktiv || undefined} onClick={onClick}>
      {children}
      {hinweis && (
        <SchlossHinweis
          key={hinweis.n}
          x={hinweis.x}
          y={hinweis.y}
          text={hinweis.text}
          onClose={() => setHinweis(null)}
        />
      )}
    </div>
  );
}
